/**
 * Condiciones de facturación de oferta y pedido (#905): el catálogo que pinta el bloque y las
 * reglas de qué está pendiente de autorizar.
 *
 * Qué condiciones requieren autorización NO es fijo: lo decide Ajustes › Autorizaciones de
 * condiciones de facturación, y la pantalla lo recibe de la API (`Autorizaciones.reglas` o el
 * `reglas` del estado de las condiciones). `REQUIEREN_POR_DEFECTO` es solo el valor de partida,
 * para pintar algo mientras la API no ha contestado.
 *
 * Las reglas de valor son **copia** de `CondicionesFacturacionReglas.java` y están aquí solo para
 * que la pantalla pinte el ámbar al instante, mientras se edita y antes de guardar. Lo que decide
 * —si la oferta se puede enviar, qué se puede pedir— lo contesta la API sobre lo guardado
 * (`Autorizaciones.estadoCondiciones`). Si se cambia una regla, se cambia en los dos sitios.
 *
 * Un valor nulo es el valor por defecto: los pedidos y ofertas que ya existían no tienen el bloque y
 * se leen así.
 */

/**
 * Cómo se nombra a quien autoriza si la API aún no ha contestado. Sin nombres: un nombre escrito aquí
 * se seguía enseñando aunque ya no correspondiera a nadie (#905, prueba de ABM del 28/10/2026).
 */
export const AUTORIZADORES_POR_DEFECTO = 'los autorizadores generales';

/** Las que requieren autorización mientras Ajustes no diga otra cosa: las que retrasan la factura. */
export const REQUIEREN_POR_DEFECTO = ['COMO_FACTURAR', 'RETRASO_CLIENTE', 'FACTURA_AGRUPADA', 'SEGUNDAS_PEDIDO_TOTAL', 'CONFIRMAR_IMPORTE'];

/** Pasados estos días se factura lo ejecutado; «Ampliar» exige más. */
export const DIAS_RETRASO_POR_DEFECTO = 30;

/**
 * Las condiciones de la lista aprobada por Dirección, en el orden de la pantalla. `clave` es la de
 * la cola y la de Ajustes. Cualquier opción distinta de `defecto` es la que puede necesitar
 * autorización; `nota` resume qué efecto tiene. `retrasa` dice si su naturaleza es retrasar la
 * factura (se dice así en el aviso). Una condición con `fijo` no se elige: se enseña como dato.
 */
export const CONDICIONES_PRINCIPALES = [
  {
    id: 'comoFacturar', campo: 'comoFacturar', clave: 'COMO_FACTURAR', nombre: 'Cómo se factura', retrasa: true,
    porDefecto: 'Por línea ejecutada', defecto: 'POR_LINEA',
    opciones: [
      { valor: 'POR_LINEA', nombre: 'Por línea ejecutada' },
      { valor: 'PEDIDO_COMPLETO', nombre: 'Pedido completo' },
      { valor: 'AL_ENTREGAR_SEGUNDAS', nombre: 'Al entregar las segundas' }
    ]
  },
  {
    id: 'numeroPedido', campo: 'ordenCompra', clave: 'NUMERO_PEDIDO', enCabecera: true, nombre: 'Nº de pedido del cliente',
    porDefecto: 'No se exige', defecto: false,
    opciones: [
      { valor: false, nombre: 'No se exige' },
      { valor: true, nombre: 'Se exige' }
    ]
  },
  {
    id: 'documentacion', campo: 'documentacion', clave: 'DOCUMENTACION', nombre: 'Documentación del cliente',
    porDefecto: 'Según el servicio', defecto: 'SEGUN_SERVICIO',
    opciones: [
      { valor: 'SEGUN_SERVICIO', nombre: 'Según el servicio' },
      { valor: 'PACTADA', nombre: 'Se pacta' }
    ]
  },
  {
    // Dirección, 27/10/2026: SIMECAL no incluye las segundas en la primera. Con un solo valor, un
    // selector parecería una elección que no existe, así que va como dato fijo. La API rechaza
    // «Incluidas en la primera» y migra lo que se guardó antes.
    id: 'segundas', campo: 'segundas', nombre: 'Segundas inspecciones', defecto: 'APARTE',
    fijo: 'Se facturan siempre aparte', fijoDetalle: 'SIMECAL no permite incluirlas en la primera.',
    opciones: []
  },
  {
    id: 'retraso', campo: 'retraso', clave: 'RETRASO_CLIENTE', nombre: 'Retraso del cliente en la ejecución', retrasa: true,
    porDefecto: '30 días', defecto: 'TREINTA_DIAS',
    opciones: [
      { valor: 'TREINTA_DIAS', nombre: '30 días' },
      { valor: 'AMPLIAR', nombre: 'Ampliar' }
    ]
  },
  {
    id: 'facturacionAutomatica', campo: 'facturacionAutomatica', clave: 'FACTURACION_AUTOMATICA', nombre: 'Facturación automática',
    porDefecto: 'Sí', defecto: true,
    opciones: [
      { valor: true, nombre: 'Sí' },
      { valor: false, nombre: 'No' }
    ]
  }
];

/** «Otras condiciones del cliente» (aprobadas el 25/10), como casillas. */
export const OTRAS_CONDICIONES = [
  { campo: 'facturaAgrupadaMes', clave: 'FACTURA_AGRUPADA', retrasa: true,
    nombre: 'Factura única o agrupada a fin de mes',
    ejemplo: '«SE AGRUPAN TODAS LAS FACTURAS EN 1 SOLA A FINAL DE MES»' },
  { campo: 'segundasPedidoTotal', clave: 'SEGUNDAS_PEDIDO_TOTAL', retrasa: true,
    nombre: 'Las segundas generan pedido por el total',
    ejemplo: '«NO FACTURAR LAS SEGUNDAS VISITAS, GENERAN UN PEDIDO POR EL TOTAL»' },
  { campo: 'confirmarImporte', clave: 'CONFIRMAR_IMPORTE', retrasa: true,
    nombre: 'Confirmar el importe con el cliente antes de facturar',
    ejemplo: '«CONFIRMAR IMPORTE»' },
  { campo: 'administracionPublicaFace', clave: 'ADMINISTRACION_PUBLICA', nombre: 'Administración pública: FACe con DIR3',
    ejemplo: 'Oficina contable, órgano gestor y unidad tramitadora' },
  { campo: 'portalProveedores', clave: 'PORTAL_PROVEEDORES', nombre: 'Factura por el portal de proveedores del cliente',
    ejemplo: 'Administración la sube tras emitirla' },
  { campo: 'adelantarInforme', clave: 'ADELANTAR_INFORME', nombre: 'Adelantar el informe por correo a una persona del cliente',
    ejemplo: '«ADELANTAR INFORME POR EMAIL A…»' }
];

/** Todas las que pueden requerir autorización, con su nombre, en el orden de la pantalla. */
export const CONDICIONES_CONFIGURABLES = CONDICIONES_PRINCIPALES.filter(c => c.clave)
  .concat(OTRAS_CONDICIONES)
  .map(c => ({ clave: c.clave, nombre: c.nombre, retrasa: !!c.retrasa }));

/**
 * El valor que tiene la condición si no es el de por defecto, o `null`. No mira Ajustes: es la
 * «moneda» de la autorización, como en la API. `ordenCompra` es el de la cabecera del documento.
 */
export function valorQueRetrasa(clave, c, ordenCompra) {
  if (clave === 'NUMERO_PEDIDO') return ordenCompra === true ? 'SE_EXIGE' : null;
  if (!c) return null;
  switch (clave) {
    case 'COMO_FACTURAR':
      return c.comoFacturar && c.comoFacturar !== 'POR_LINEA' ? c.comoFacturar : null;
    case 'DOCUMENTACION':
      return c.documentacion === 'PACTADA' ? 'PACTADA:' + c.documentacionDias : null;
    case 'RETRASO_CLIENTE':
      // «Ampliar» con 30 días o menos no amplía nada y la API no lo deja guardar: no se pinta en
      // ámbar ni se cuenta como pendiente; el bloque dice al lado qué falta.
      return c.retraso === 'AMPLIAR' && Number(c.retrasoDias) > DIAS_RETRASO_POR_DEFECTO ? 'AMPLIAR:' + c.retrasoDias : null;
    case 'FACTURACION_AUTOMATICA':
      return c.facturacionAutomatica === false ? 'NO' : null;
    case 'FACTURA_AGRUPADA':
      return c.facturaAgrupadaMes === true ? 'SI' : null;
    case 'SEGUNDAS_PEDIDO_TOTAL':
      return c.segundasPedidoTotal === true ? 'SI' : null;
    case 'CONFIRMAR_IMPORTE':
      return c.confirmarImporte === true ? 'SI' : null;
    case 'ADMINISTRACION_PUBLICA':
      return c.administracionPublicaFace === true ? 'SI' : null;
    case 'PORTAL_PROVEEDORES':
      return c.portalProveedores === true ? 'SI' : null;
    case 'ADELANTAR_INFORME':
      return c.adelantarInforme === true ? 'SI' : null;
    default:
      return null;
  }
}

/** La última solicitud de ese mismo valor, o `null`. */
export function ultimaDecision(c, clave, valor) {
  const lista = (c && c.autorizaciones) || [];
  let ultima = null;
  lista.forEach(a => {
    if (a.clave === clave && a.valor === valor) ultima = a;
  });
  return ultima;
}

/**
 * En qué punto está una condición: `null` si está en su valor por defecto o Ajustes dice que no
 * requiere autorización; si no, `{ valor, estado, decision }` con estado AUTORIZADA, SOLICITADA,
 * RECHAZADA o SIN_SOLICITAR.
 */
export function situacion(c, clave, ordenCompra, requieren) {
  if ((requieren || REQUIEREN_POR_DEFECTO).indexOf(clave) === -1) return null;
  const valor = valorQueRetrasa(clave, c, ordenCompra);
  if (valor === null) return null;
  const decision = ultimaDecision(c, clave, valor);
  let estado = 'SIN_SOLICITAR';
  if (decision && decision.estado === 'AUTORIZADA') estado = 'AUTORIZADA';
  else if (decision && decision.estado === 'PENDIENTE') estado = 'SOLICITADA';
  else if (decision && decision.estado === 'RECHAZADA') estado = 'RECHAZADA';
  return { valor, estado, decision };
}

/** Las claves que requieren autorización y no la tienen con su valor actual. */
export function pendientes(c, ordenCompra, requieren) {
  return CONDICIONES_CONFIGURABLES.map(x => x.clave).filter(clave => {
    const s = situacion(c, clave, ordenCompra, requieren);
    return s && s.estado !== 'AUTORIZADA';
  });
}

/** Lo que el bloque guarda, para saber si hay cambios sin guardar. */
export function firma(c, ordenCompra, numeroOrdenCompra) {
  const v = c || {};
  return JSON.stringify([
    v.comoFacturar || 'POR_LINEA', v.documentacion || 'SEGUN_SERVICIO',
    v.documentacion === 'PACTADA' ? [v.documentacionPactada || '', v.documentacionDias || null] : null,
    v.retraso || 'TREINTA_DIAS', v.retraso === 'AMPLIAR' ? v.retrasoDias || null : null,
    v.facturacionAutomatica !== false,
    !!v.facturaAgrupadaMes, !!v.segundasPedidoTotal, !!v.confirmarImporte,
    !!v.administracionPublicaFace, !!v.portalProveedores, !!v.adelantarInforme,
    !!ordenCompra, ordenCompra ? (numeroOrdenCompra || '') : ''
  ]);
}

export function fechaCorta(fecha) {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return '';
  const dos = n => (n < 10 ? '0' : '') + n;
  return dos(d.getDate()) + '/' + dos(d.getMonth() + 1) + '/' + d.getFullYear();
}

export default {
  AUTORIZADORES_POR_DEFECTO,
  REQUIEREN_POR_DEFECTO,
  DIAS_RETRASO_POR_DEFECTO,
  CONDICIONES_PRINCIPALES,
  OTRAS_CONDICIONES,
  CONDICIONES_CONFIGURABLES,
  valorQueRetrasa,
  ultimaDecision,
  situacion,
  pendientes,
  firma,
  fechaCorta
};
